import { isTier1Offer } from 'api/mappers/offerUrgencyLabelMap'
import {
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_ALWAYS_ON_ANALYTICS,
  OFFER_TYPE_BED_BANK,
  OFFER_TYPE_BED_BANK_ANALYTICS,
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_HOTEL_ANALYTICS,
  PRODUCT_TYPE_ULTRALUX,
} from 'constants/offer'
import { checkGatedOffer } from 'lib/offer/offerUtils'
import determineOfferFeatureSymbol from 'lib/offer/determineOfferFeatureSymbol'
import type { OfferDetailResult } from 'tripPlanner/api/types'
import {
  mapDrawerHotelPricingFields,
  mapImages,
  mapOfferDetailInclusions,
  mapOfferDetailAmenities,
} from 'tripPlanner/mappers/offerDetailSharedFields'

type CarouselOffer = App.HotelOffer | App.BedbankOffer

export function mapOfferDetailToCarouselAmenities(detail: OfferDetailResult): Array<App.OfferInclusion> {
  return (mapOfferDetailAmenities(detail) ?? []).map((amenity, index) => ({
    id: `${detail.offerId}-amenity-${index}-${amenity.name}`,
    description: amenity.name,
    symbol: determineOfferFeatureSymbol(amenity.icon ?? undefined) ?? determineOfferFeatureSymbol(amenity.name),
  }))
}

/**
 * Price/value/discount legs for the one synthetic package, taken verbatim from the drawer's
 * own pricing mapper so the carousel card and the drawer can never disagree on a number.
 */
type CarouselPricing = ReturnType<typeof mapDrawerHotelPricingFields>

function nightsFor(detail: OfferDetailResult): number {
  return detail.pricing?.durationNights ?? detail.duration ?? 1
}

function mapRating(detail: OfferDetailResult): App.OfferRating | undefined {
  // The DTO carries `rating` as a bare number and `reviewsSource` as a sibling string;
  // `OfferRating`/`isOfferRatingDisplayable` want them as one object.
  //
  // Truthy, not `=== undefined`, to match `hotelOfferMap.ts`'s `if (property.rating)` on the
  // client-fetched path this replaces. svc-trip runs with `validateResponses: false` in
  // production, so a null really can arrive — and `=== undefined` let it through and built
  // `{ score: null }`, making this mapper strictly less null-safe than the one it supersedes.
  // A 0 rating is unrepresentable upstream and would be meaningless here anyway.
  if (!detail.rating) {
    return undefined
  }

  return {
    score: detail.rating,
    reviewsTotal: detail.reviewsTotal ?? 0,
    reviewsSource: detail.reviewsSource,
  }
}

/**
 * Every field on the DTO's badge is optional, while `App.OfferBadge` requires most of them.
 * `OfferBadgeLabel` renders nothing at all without `tagText`, so a badge that lacks it is
 * dropped; the remaining fields fall back to empty strings, which that component already
 * treats as "no tooltip" / default icon.
 */
function mapBadge(badge: OfferDetailResult['badge']): App.OfferBadge | undefined {
  if (!badge?.tagText) {
    return undefined
  }

  return {
    name: badge.name ?? '',
    image: badge.image ?? '',
    tagIcon: badge.tagIcon ?? '',
    tagText: badge.tagText,
    tagTooltip: badge.tagTooltip ?? '',
    url: badge.url,
  }
}

/**
 * The DTO carries the list-visibility window directly (Task A9), so this no longer has to be
 * reverse-engineered from the countdown tag's rendered text. That matters beyond tidiness:
 * svc-public-offer only emits the countdown tag in the window's final 4 days, so the tag-text
 * derivation was blind for the rest of the offer's life.
 */
function mapVisibilitySchedule(detail: OfferDetailResult): App.OfferSchedule | undefined {
  const schedule = detail.visibilitySchedule
  // `App.OfferSchedule` requires both bounds; a half-populated window is not renderable.
  if (!schedule?.start || !schedule.end) {
    return undefined
  }

  return { start: schedule.start, end: schedule.end }
}

const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24
const NEW_DEAL_MAX_DAYS_LIVE = 3
const NEW_DEAL_MIN_DAYS_TO_END = 4

/**
 * Rebuilds the two urgency labels this card can render from the raw inputs Task A9 carries,
 * mirroring `api/mappers/offerUrgencyLabelMap` — the client-side path's own rules — rather
 * than the rendered `urgencyTags` text, which cannot express either of them:
 * `lux_plus_general_release` always arrives upstream with a null message and so never reaches
 * `urgencyTags` at all, and `new` is computed from the deal tier and the window, not sent.
 *
 * `left` is deliberately not emitted: `LEOfferCard` filters it out of the label row
 * (`offer.urgencyTags.filter(label => label.type !== 'left')`) and renders its countdown from
 * `visibilitySchedule` instead, which this mapper already populates.
 */
function mapUrgencyLabels(detail: OfferDetailResult): Array<App.OfferUrgencyLabel> {
  const labels: Array<App.OfferUrgencyLabel> = []
  const schedule = detail.visibilitySchedule

  if (isTier1Offer(detail.dealTier) && schedule?.start && schedule.end) {
    const now = Date.now()
    const daysSinceDealIsLive = Math.floor((now - new Date(schedule.start).getTime()) / MILLISECONDS_PER_DAY)
    const daysToDealEnd = Math.floor((new Date(schedule.end).getTime() - now) / MILLISECONDS_PER_DAY)

    if (
      daysSinceDealIsLive >= 0 &&
      daysSinceDealIsLive < NEW_DEAL_MAX_DAYS_LIVE &&
      daysToDealEnd > NEW_DEAL_MIN_DAYS_TO_END
    ) {
      labels.push({ type: 'new' })
    }
  }

  if (detail.urgencyLabelTypes?.includes('lux_plus_general_release')) {
    // `OfferLabels` renders this one only when `end` is set, and that end is the moment early
    // access finishes — i.e. the list-visibility *start*. The early-access start itself
    // (`schedules.luxPlus.start`) is not on the DTO and is not read by the label.
    labels.push({ type: 'lux_plus_general_release', end: schedule?.start })
  }

  return labels
}

function mapCarouselPackage(
  detail: OfferDetailResult,
  pricing: CarouselPricing,
): App.HotelPackage {
  const nights = nightsFor(detail)

  return {
    // The DTO carries no LE package/room identifiers; the loyalty points-earn request
    // treats a missing package id the same way (`pkg?.id ?? ''`).
    id: '',
    lePackageId: '',
    offerId: detail.offerId,
    uniqueKey: `${detail.offerId}-svc-trip`,
    name: detail.name,
    roomOccupancy: '',
    shouldDisplayValue: detail.shouldDisplayValue ?? false,
    // `pricing.price` is already the complete stay total for the requested dates and
    // occupancy, so there is no separate fee leg to add. Keeping the fee legs at 0 makes
    // `useOfferPackagePrice`'s `pkg.price + pkg.propertyFees` render the DTO amount exactly.
    price: pricing.price?.total ?? 0,
    memberPrice: pricing.luxPlusPrice ?? 0,
    value: pricing.value ?? 0,
    memberValue: pricing.memberValue ?? 0,
    // Carried verbatim from the DTO. Note the card does not render this number directly:
    // `useOfferPackagePrice` recomputes it as `getValueOffPercent(value, price)` (floor),
    // so a DTO percent that was rounded differently upstream can render one point lower
    // than the drawer, which does print this field. Not something to paper over here —
    // distorting `value` to force a match would corrupt the value leg itself.
    discountPercent: pricing.discountPercent ?? 0,
    taxesAndFees: 0,
    propertyFees: 0,
    surcharge: 0,
    bundleDiscountPercent: 0,
    durationLabel: '',
    duration: nights,
    inclusions: mapOfferDetailInclusions(detail),
    inclusionsAmount: 0,
    totalInclusionsAmount: 0,
    bonusInclusions: [],
    showInclusionsValidityTag: false,
    boardCode: 'roomonly',
    loyalty: {},
    // Flash reads the encrypted margin off the package, LPC/Always-On off the room rate
    // (see `getHotelEncryptedMargin`). The DTO carries one token for whichever applies.
    mx: detail.mx,
    roomType: {
      id: '',
      name: detail.name,
      description: '',
      bedGroups: [],
      amenities: detail.offerType === OFFER_TYPE_ALWAYS_ON
        ? mapOfferDetailToCarouselAmenities(detail).map(amenity => ({ ...amenity, group: '' }))
        : [],
      images: mapImages(detail.images),
      additionalGuestAmountDescription: '',
      stats: { booked: 0 },
    },
    roomRate: {
      id: '',
      ratePlanId: detail.ratePlanId ?? '',
      cancellationPolicy: { policyDetail: [] },
      cancellationPolicies: [],
      extraGuestSurcharges: [],
      capacities: [],
      includedGuests: [],
      inclusionsHideValue: detail.inclusionsHideValue ?? false,
      isPackaged: false,
      packagedRatePlanId: null,
      isBaseRate: true,
      schedules: [],
      isReservableForZero: false,
      mx: detail.mx,
    },
  }
}

function mapToHotelOffer(detail: OfferDetailResult): App.HotelOffer {
  const pricing = mapDrawerHotelPricingFields(detail.pricing, nightsFor(detail), detail.currencyCode)
  const pkg = mapCarouselPackage(detail, pricing)
  const images = mapImages(detail.images)
  const isAlwaysOn = detail.offerType === OFFER_TYPE_ALWAYS_ON
  const isUltraLux = !!detail.isUltraLux

  // `getLPCRegionLOS`'s callers pair the marketed LOS with `packages.some(p => p.duration ===
  // LOS)`, which this card cannot run — it has one synthetic package and no options list — so
  // svc-trip answers it in `hasRegionLosOption`. Both guards are required affirmatively:
  // absence is not evidence, and for `isPartnerProperty` the shared gate reads absence as
  // permissive. Pinned by the composed-gate tests in `ChatHotelCarouselPrice.test.tsx`.
  const hasAdvertisableRegionLos = detail.hasRegionLosOption === true && detail.isPartnerProperty === false

  let productType: App.ProductType = isAlwaysOn ? OFFER_TYPE_ALWAYS_ON : OFFER_TYPE_HOTEL
  if (isUltraLux) {
    // `isUltraLuxOffer` reads productType, not property.isUltraLux — mirrors hotelOfferMap.
    productType = PRODUCT_TYPE_ULTRALUX
  }

  const offer: App.HotelOffer = {
    id: detail.offerId,
    name: detail.name,
    slug: detail.slug ?? '',
    type: isAlwaysOn ? OFFER_TYPE_ALWAYS_ON : OFFER_TYPE_HOTEL,
    parentType: OFFER_TYPE_HOTEL,
    productType,
    analyticsType: isAlwaysOn ? OFFER_TYPE_ALWAYS_ON_ANALYTICS : OFFER_TYPE_HOTEL_ANALYTICS,
    typeLabel: OFFER_TYPE_HOTEL,
    walledGarden: isAlwaysOn && !isUltraLux,
    description: detail.description ?? '',
    additionalDescription: '',
    // `LEOfferCard` composes the tile's location line from heading + subheading, not from
    // `location`; svc-trip returns one already-composed string, so it fills the heading.
    location: detail.location,
    locationHeading: detail.location,
    locations: [],
    images,
    image: images[0] ?? {},
    saleUnit: detail.saleUnit ?? '',
    saleUnitLong: detail.saleUnit ?? '',
    packages: [pkg],
    lowestPricePackage: pkg,
    // The shortest bookable stay, which is what `getHotelOfferMinNights` -> `resolveHotelStayNights`
    // uses as AddHotelToTripButton's night floor. svc-trip derives it from the offer's options
    // (`Math.min(...options.duration)`), reproducing the rule `hotelOfferMap` applies on the
    // client-fetched path.
    //
    // Falling back to the priced stay only when the DTO carries none: that fallback WAS the whole
    // behaviour, and it silently raised the floor — an offer selling 3/5/7-night packages that was
    // priced at 6 could not be added as a 3-night stay (review #37389 [1/50]).
    //
    // Still never the region-marketed LOS: that would make a card whose price line was computed
    // for N nights add a stay of a different length. The "Stays from N nights available" caption
    // reads the LOS off `property.currentRegionMarketedLos` instead; see `ChatHotelCarouselPrice`.
    minDuration: detail.minDurationNights ?? pkg.duration,
    durationLabel: '',
    tileDurationLabel: '',
    tileInclusions: mapOfferDetailInclusions(detail),
    badge: mapBadge(detail.badge),
    campaignTags: detail.campaignTags ?? [],
    luxPlus: detail.luxPlus ?? {},
    isDiscountPillHidden: detail.isDiscountPillHidden ?? false,
    shouldDisplayValue: detail.shouldDisplayValue ?? false,
    // Carried verbatim so `getLPCRegionLOS`'s own `!isPartnerProperty` conjunct decides on the
    // real value rather than on a field this mapper never set.
    isPartnerProperty: detail.isPartnerProperty,
    isSoldOut: pricing.unavailableReason === 'sold_out',
    visibilitySchedule: mapVisibilitySchedule(detail),
    urgencyTags: mapUrgencyLabels(detail),
    property: {
      id: detail.offerId,
      name: detail.name,
      address: detail.location,
      latitude: detail.geoLocation?.lat ?? 0,
      longitude: detail.geoLocation?.lng ?? 0,
      timezoneOffset: 0,
      reviews: [],
      infantPolicy: '',
      // Four fields, two consumers. `countryCode`/`administrativeAreaLevel1` are the loyalty
      // points earnKey's location segment (`generateLoyaltyPointsCalculatorHotelOptions`) —
      // without them a country- or state-scoped bonus campaign silently stops matching and the
      // card understates the points earned, with no error anywhere.
      //
      // `locality`/`country` are what `getHotelOrTourOfferItem` emits as `destinationCity` and
      // `destinationCountry` on BOTH productImpression and productClick. Carrying only the
      // loyalty pair emptied both on every carousel card (review #37389 [2/50]).
      geoData: {
        countryCode: detail.geoData?.countryCode,
        administrativeAreaLevel1: detail.geoData?.administrativeAreaLevel1,
        locality: detail.geoData?.locality,
        country: detail.geoData?.country,
      },
      maxChildAge: 0,
      maxInfantAge: 0,
      maxTeenagerAge: 0,
      useDynamicTaxesFees: false,
      useDynamicCancellationPolicies: false,
      useDynamicOccupancy: false,
      useDynamicRates: false,
      useDynamicAvailability: false,
      // What `getLPCRegionLOS` returns for an Always-On offer — `ChatHotelCarouselPrice` reads
      // the LOS from here and deliberately *not* from `minDuration`, which is the shortest
      // bookable stay and feeds the add-to-trip night floor. The field is a required number, and
      // 0 is the value getLPCRegionLOS callers already read as "no marketed LOS", so it is the
      // honest stand-in both when the DTO carries none and when the guards above withhold it.
      currentRegionMarketedLos: hasAdvertisableRegionLos ? detail.currentRegionMarketedLos ?? 0 : 0,
      hideChildPrices: false,
      isUltraLux,
      rating: mapRating(detail),
    },
    // Fields with no counterpart on the offer-detail DTO. They are all content/flow flags
    // the pre-edit carousel card never renders — left at their inert defaults rather than
    // guessed at.
    facilities: '',
    finePrint: '',
    gettingThere: '',
    highlights: '',
    whatWeLike: '',
    holidayTypes: [],
    insuranceCountries: [],
    defaultOptions: [],
    whitelistedCarrierCodes: [],
    flightPrices: {},
    flightDestinationPort: '',
    flightCacheDisabled: false,
    flightsWarningEnabled: false,
    offerFlightsEnabled: false,
    experiencesInFlow: false,
    experiencesCurated: false,
    showOnlyExperiencesCurated: false,
    hasBuyNowBookLater: false,
    hasBuyNowBookLaterPackages: false,
    canEarnPartnershipPoints: false,
    daysBeforeCheckInChangesDisallowed: 0,
    numberOfDateChangesAllowed: 'Unlimited',
    isAgentHubExclusive: false,
    loyalty: { hasPackageTypeUpgrades: false, hasRoomTypeUpgrades: false },
    stats: { booked: 0, views: 0, recentPurchases: [] },
  }

  // Mirrors hotelOfferMap's own final step: a hardcoded gated offer id is walled regardless
  // of type. Applied after construction because `checkGatedOffer` takes a whole offer.
  offer.walledGarden = checkGatedOffer(offer) || offer.walledGarden

  return offer
}

function mapToBedbankOffer(detail: OfferDetailResult): App.BedbankOffer {
  const pricing = mapDrawerHotelPricingFields(detail.pricing, nightsFor(detail), detail.currencyCode)
  const images = mapImages(detail.images)
  const price = pricing.price?.total
  const facilityNames = mapOfferDetailToCarouselAmenities(detail).map(amenity => amenity.description)
  // Mirrors `bedbankOfferMap`, which reads `promotions.length` for both of these. `isMetasearch`
  // is not a factor: it comes from the caller's own `source` request param, which the carousel's
  // pre-migration path never passes (`bedBankOfferMap(serverOffer, undefined, …)`), so it is
  // always false on this surface. An absent flag (a pre-deploy cached detail) reads as walled.
  const hasPromotions = detail.hasPromotions === true

  return {
    id: detail.offerId,
    name: detail.name,
    slug: detail.slug ?? '',
    type: OFFER_TYPE_BED_BANK,
    parentType: OFFER_TYPE_HOTEL,
    productType: hasPromotions ? 'limited_time_special' : OFFER_TYPE_BED_BANK,
    analyticsType: OFFER_TYPE_BED_BANK_ANALYTICS,
    typeLabel: OFFER_TYPE_HOTEL,
    walledGarden: !hasPromotions,
    description: detail.description ?? '',
    images,
    image: images[0] ?? {},
    imagesCount: images.length,
    saleUnit: detail.saleUnit ?? 'room',
    saleUnitLong: detail.saleUnit ?? 'room',
    campaignTags: detail.campaignTags ?? [],
    // Bedbank has no LuxPlus+ configuration at all — an empty object, exactly as
    // `bedBankOfferMap` already does for the client-side path.
    luxPlus: {},
    // `sell` is the "from" price the bedbank tile prices off. Withheld entirely when
    // svc-trip reports no amount, rather than rendering a fabricated 0.
    sell: price === undefined ? undefined : {
      price,
      // svc-trip quotes an already-marked price and carries no channel markup of its own, so the
      // base equals the price: `useBedbankOfferSell` then rescales it without re-marking.
      basePrice: price,
      // `App.BedbankSellPriceInfo.value` is a required number, so it cannot be left nullish
      // the way the DTO leaves it. Defaulting to the price reproduces `ratePriceDefault`'s
      // own `?? hotelPrice` fallback, which a literal 0 would silently bypass.
      value: pricing.value ?? price,
      discountPercentage: pricing.discountPercent ?? 0,
      taxesAndFees: 0,
      propertyFees: 0,
      los: nightsFor(detail),
    },
    // `BedbankOfferCard` renders `description={offer.amenities}` on the small card the chat
    // carousel uses, and `App.BedbankOffer.amenities` is a copy *string*, not a list — so the
    // DTO's amenity names are joined for it. `popularFacilities` carries the same names as a
    // list for the drawer-tile path (`getPrioritizedBedbankAmenities`).
    amenities: facilityNames.length ? facilityNames.join(', ') : undefined,
    popularFacilities: facilityNames,
    packages: [],
    promotions: [],
    property: {
      id: detail.offerId,
      name: detail.name,
      location: detail.location,
      // svc-trip returns one already-composed location string; the bedbank tile builds its
      // location line from `address.city`/`countryName`, so the composed string goes in the
      // city slot and the parts the DTO does not carry stay empty.
      //
      // `countryCode` and the two state/province fields are the exception: they are the
      // loyalty earn key's location segment. `generateLoyaltyPointsCalculatorBedbankOptions`
      // reads `address.countryCode` and hands the whole address to
      // `getLoyaltyBedbankPropertyStateProvince`, so both raw fields are carried through
      // unresolved and that function's `stateProvinceCode ?? stateProvinceName` fallback stays
      // the one that decides. Left blank/undefined when the DTO carries no address — the
      // earn key then has no location segment, which is the honest "unknown".
      address: {
        line1: '',
        city: detail.location,
        postalCode: '',
        countryCode: detail.propertyAddress?.countryCode ?? '',
        countryName: '',
        obfuscationRequired: false,
        stateProvinceCode: detail.propertyAddress?.stateProvinceCode,
        stateProvinceName: detail.propertyAddress?.stateProvinceName,
      },
      timezone: '',
      latitude: detail.geoLocation?.lat ?? 0,
      longitude: detail.geoLocation?.lng ?? 0,
      phone: '',
      rating: mapRating(detail),
    },
    // Fields with no counterpart on the offer-detail DTO.
    attractions: '',
    metaDescription: '',
    tagline: '',
    additionalPromoContent: '',
    facilityGroups: [],
    singleUnit: false,
    promoteAsBundle: false,
    displayPricingAsPerPerson: false,
    flights: { flightsEnabled: false },
    stats: { booked: 0, views: 0, recentPurchases: [] },
  }
}

/**
 * Adapts svc-trip's offer-detail DTO into the offer shape the existing carousel cards
 * already render, so the pre-edit carousel can show svc-trip's own price rather than
 * re-fetching and re-pricing the offer client-side.
 *
 * Every price/value/discount leg comes from `mapDrawerHotelPricingFields` — the same
 * function the hotel drawer uses — so the two surfaces cannot drift.
 */
export function mapOfferDetailToCarouselOffer(detail: OfferDetailResult): CarouselOffer {
  return detail.offerType === OFFER_TYPE_BED_BANK ? mapToBedbankOffer(detail) : mapToHotelOffer(detail)
}
